import React, { lazy, Suspense } from 'react';
import { RouteObject, Navigate } from 'react-router-dom';
import HomePage from '../pages/home/page';
import ForumPage from '../pages/forum/page';
import ForumPostPage from '../pages/forum/post/page';
import PostDetailPage from '../pages/post/detail/page';
import MessagesPage from '../pages/messages/page';
import ProfilePage from '../pages/profile/page';
import NotFoundPage from '../pages/NotFound';
import DramasPage from '../pages/dramas/page';
import DramaDetailPage from '../pages/dramas/detail/page';
import AIProjectDetailPage from '../pages/dramas/project/page';
import CreatorsPage from '../pages/creators/page';
import CreatorDetailPage from '../pages/creators/detail/page';
import GroupsPage from '../pages/groups/page';
import GroupDetailPage from '../pages/groups/detail/page';
// Import detail pages for forum categories
import ReviewsDetailPage from '../pages/forum/reviews/detail/page';
import RecommendationsDetailPage from '../pages/forum/recommendations/detail/page';
import BehindScenesDetailPage from '../pages/forum/behind-scenes/detail/page';
import GeneralDetailPage from '../pages/forum/general/detail/page';
import CreatorsDetailPage from '../pages/forum/creators/detail/page';
import NewsForumDetailPage from '../pages/forum/news/detail/page';
import AiDramasDetailPage from '../pages/forum/ai-dramas/detail/page';
import AuthCallbackPage from '../pages/auth/callback/page';
import SettingsPage from '../pages/settings/page';
import RedirectHandler from '../components/feature/RedirectHandler';
import UserProfilePage from '../pages/user/page';
import NewsArchivePage from '../pages/news/page';
import NewsDetailPage from '../pages/news/detail/page';
import AdminRouteGuard from '../components/admin/AdminRouteGuard';
import AdminLayout from '../components/admin/AdminLayout';

// Admin Pages
const AdminLoginPage = lazy(() => import('../pages/admin/login/page'));
const AdminDashboardPage = lazy(() => import('../pages/admin/dashboard/page'));
const AdminUsersPage = lazy(() => import('../pages/admin/users/page'));
const AdminAIDramaCreatorsPage = lazy(() => import('../pages/admin/users/ai-drama-creators/page'));
const AdminCreatorCollectionPage = lazy(() => import('../pages/admin/users/creator-collection/page'));
const AdminContentPage = lazy(() => import('../pages/admin/content/page'));
const AdminQueuePage = lazy(() => import('../pages/admin/content/queue/page'));
const AdminReportsPage = lazy(() => import('../pages/admin/content/reports/page'));
const AdminGroupsPage = lazy(() => import('../pages/admin/content/groups/page'));
const AdminEventsPage = lazy(() => import('../pages/admin/content/events/page'));
const AdminAuditLogPage = lazy(() => import('../pages/admin/content/audit-log/page'));
const AdminNewsPage = lazy(() => import('../pages/admin/content/news/page'));
const AdminDramasPage = lazy(() => import('../pages/admin/content/dramas/page'));
const AdminDramaReviewsPage = lazy(() => import('../pages/admin/content/reviews/page'));
const AdminGroupDiscussionsPage = lazy(() => import('../pages/admin/content/discussions/page'));
const AdminSettingsPage = lazy(() => import('../pages/admin/settings/page'));
const AdminBulkNewsPage = lazy(() => import('../pages/admin/content/bulk-news/page'));
const AdminBulkDramasPage = lazy(() => import('../pages/admin/content/bulk-dramas/page'));

const AdminUsersCreatorCollection = lazy(() => import('../pages/admin/users/creator-collection/page').then(m => ({ default: m.default })));
const AdminUsersBulkCreator = lazy(() => import('../pages/admin/users/bulk-creator/page').then(m => ({ default: m.default })));
const AdminUsersAIDramaCreators = lazy(() => import('../pages/admin/users/ai-drama-creators/page').then(m => ({ default: m.default })));

const routes: RouteObject[] = [
  {
    path: '/',
    element: (
      <>
        <RedirectHandler />
        <HomePage />
      </>
    ),
  },
  {
    path: '/news',
    element: (
      <>
        <RedirectHandler />
        <NewsArchivePage />
      </>
    ),
  },
  {
    path: '/news/:urlParam',
    element: (
      <>
        <RedirectHandler />
        <NewsDetailPage />
      </>
    ),
  },
  {
    path: '/forum',
    element: (
      <>
        <RedirectHandler />
        <ForumPage />
      </>
    ),
  },

  // Forum post detail routes: /forum/[category]/[shortId]-[slug]
  {
    path: '/forum/general/:urlParam',
    element: (
      <>
        <RedirectHandler />
        <GeneralDetailPage />
      </>
    ),
  },
  {
    path: '/forum/reviews/:urlParam',
    element: (
      <>
        <RedirectHandler />
        <ReviewsDetailPage />
      </>
    ),
  },
  {
    path: '/forum/creators/:urlParam',
    element: (
      <>
        <RedirectHandler />
        <CreatorsDetailPage />
      </>
    ),
  },
  {
    path: '/forum/recommendations/:urlParam',
    element: (
      <>
        <RedirectHandler />
        <RecommendationsDetailPage />
      </>
    ),
  },
  {
    path: '/forum/news/:urlParam',
    element: (
      <>
        <RedirectHandler />
        <NewsForumDetailPage />
      </>
    ),
  },
  {
    path: '/forum/behind-scenes/:urlParam',
    element: (
      <>
        <RedirectHandler />
        <BehindScenesDetailPage />
      </>
    ),
  },
  {
    path: '/forum/ai-dramas/:urlParam',
    element: (
      <>
        <RedirectHandler />
        <AiDramasDetailPage />
      </>
    ),
  },

  // Legacy routes
  {
    path: '/forum/post/:id',
    element: (
      <>
        <RedirectHandler />
        <ForumPostPage />
      </>
    ),
  },
  {
    path: '/post/:id',
    element: (
      <>
        <RedirectHandler />
        <PostDetailPage />
      </>
    ),
  },

  // Creator routes
  {
    path: '/creators/:urlParam',
    element: (
      <>
        <RedirectHandler />
        <CreatorDetailPage />
      </>
    ),
  },
  {
    path: '/creators',
    element: (
      <>
        <RedirectHandler />
        <CreatorsPage />
      </>
    ),
  },

  // Drama routes
  {
    path: '/dramas/project/:urlParam',
    element: (
      <>
        <RedirectHandler />
        <AIProjectDetailPage />
      </>
    ),
  },
  {
    path: '/dramas/:urlParam',
    element: (
      <>
        <RedirectHandler />
        <DramaDetailPage />
      </>
    ),
  },
  {
    path: '/dramas',
    element: (
      <>
        <RedirectHandler />
        <DramasPage />
      </>
    ),
  },

  // Groups routes
  {
    path: '/groups/:urlParam',
    element: (
      <>
        <RedirectHandler />
        <GroupDetailPage />
      </>
    ),
  },
  {
    path: '/groups',
    element: (
      <>
        <RedirectHandler />
        <GroupsPage />
      </>
    ),
  },

  {
    path: '/profile',
    element: <ProfilePage />,
  },
  {
    path: '/user/:userId',
    element: <UserProfilePage />,
  },
  {
    path: '/settings',
    element: <SettingsPage />,
  },
  {
    path: '/messages',
    element: (
      <>
        <RedirectHandler />
        <MessagesPage />
      </>
    ),
  },
  {
    path: '/auth/callback',
    element: (
      <>
        <RedirectHandler />
        <AuthCallbackPage />
      </>
    ),
  },

  // Admin Routes
  {
    path: '/staredge_admin',
    element: (
      <AdminRouteGuard>
        <AdminLayout />
      </AdminRouteGuard>
    ),
    children: [
      {
        path: 'users/creator-collection',
        element: <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="text-lg">Loading...</div></div>}><AdminUsersCreatorCollection /></Suspense>,
      },
      {
        path: 'users/bulk-creator',
        element: <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="text-lg">Loading...</div></div>}><AdminUsersBulkCreator /></Suspense>,
      },
      {
        path: 'users/ai-drama-creators',
        element: <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="text-lg">Loading...</div></div>}><AdminUsersAIDramaCreators /></Suspense>,
      },
    ],
  },
  {
    path: '/staredge_admin/login',
    element: <AdminLoginPage />,
  },
  {
    path: '/staredge_admin/dashboard',
    element: (
      <AdminRouteGuard>
        <AdminLayout>
          <AdminDashboardPage />
        </AdminLayout>
      </AdminRouteGuard>
    ),
  },
  {
    path: '/staredge_admin/users',
    element: (
      <AdminRouteGuard>
        <AdminLayout>
          <AdminUsersPage />
        </AdminLayout>
      </AdminRouteGuard>
    ),
  },
  {
    path: '/staredge_admin/users/ai-drama-creators',
    element: (
      <AdminRouteGuard>
        <AdminLayout>
          <AdminAIDramaCreatorsPage />
        </AdminLayout>
      </AdminRouteGuard>
    ),
  },
  {
    path: '/staredge_admin/users/creator-collection',
    element: (
      <AdminRouteGuard>
        <AdminLayout>
          <AdminCreatorCollectionPage />
        </AdminLayout>
      </AdminRouteGuard>
    ),
  },
  {
    path: '/staredge_admin/content',
    element: (
      <AdminRouteGuard>
        <AdminLayout>
          <AdminContentPage />
        </AdminLayout>
      </AdminRouteGuard>
    ),
  },
  {
    path: '/staredge_admin/content/queue',
    element: (
      <AdminRouteGuard>
        <AdminLayout>
          <AdminQueuePage />
        </AdminLayout>
      </AdminRouteGuard>
    ),
  },
  {
    path: '/staredge_admin/content/reports',
    element: (
      <AdminRouteGuard>
        <AdminLayout>
          <AdminReportsPage />
        </AdminLayout>
      </AdminRouteGuard>
    ),
  },
  {
    path: '/staredge_admin/content/groups',
    element: (
      <AdminRouteGuard>
        <AdminLayout>
          <AdminGroupsPage />
        </AdminLayout>
      </AdminRouteGuard>
    ),
  },
  {
    path: '/staredge_admin/content/events',
    element: (
      <AdminRouteGuard>
        <AdminLayout>
          <AdminEventsPage />
        </AdminLayout>
      </AdminRouteGuard>
    ),
  },
  {
    path: '/staredge_admin/content/audit-log',
    element: (
      <AdminRouteGuard>
        <AdminLayout>
          <AdminAuditLogPage />
        </AdminLayout>
      </AdminRouteGuard>
    ),
  },
  {
    path: '/staredge_admin/content/news',
    element: (
      <AdminRouteGuard>
        <AdminLayout>
          <AdminNewsPage />
        </AdminLayout>
      </AdminRouteGuard>
    ),
  },
  {
    path: '/staredge_admin/content/bulk-news',
    element: (
      <AdminRouteGuard>
        <AdminLayout>
          <AdminBulkNewsPage />
        </AdminLayout>
      </AdminRouteGuard>
    ),
  },
  {
    path: '/staredge_admin/content/bulk-dramas',
    element: (
      <AdminRouteGuard>
        <AdminLayout>
          <AdminBulkDramasPage />
        </AdminLayout>
      </AdminRouteGuard>
    ),
  },
  {
    path: '/staredge_admin/content/dramas',
    element: (
      <AdminRouteGuard>
        <AdminLayout>
          <AdminDramasPage />
        </AdminLayout>
      </AdminRouteGuard>
    ),
  },
  {
    path: '/staredge_admin/content/reviews',
    element: (
      <AdminRouteGuard>
        <AdminLayout>
          <AdminDramaReviewsPage />
        </AdminLayout>
      </AdminRouteGuard>
    ),
  },
  {
    path: '/staredge_admin/content/discussions',
    element: (
      <AdminRouteGuard>
        <AdminLayout>
          <AdminGroupDiscussionsPage />
        </AdminLayout>
      </AdminRouteGuard>
    ),
  },
  {
    path: '/staredge_admin/settings',
    element: (
      <AdminRouteGuard>
        <AdminLayout>
          <AdminSettingsPage />
        </AdminLayout>
      </AdminRouteGuard>
    ),
  },

  // Admin Routes
  // Only include admin routes if enabled via environment variable
  ...(import.meta.env.VITE_ENABLE_ADMIN === 'true' ? adminRoutes : []),

  {
    path: '*',
    element: (
      <>
        <RedirectHandler />
        <NotFoundPage />
      </>
    ),
  },
];

export default routes;
